import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import Spinner from './components/layout/spinner/Spinner'
import { PatientState } from './redux/reducers/auth/patient/patient-state'

const PatientSignup = lazy(() => import('./components/patient/accounts/signup/PatientSignup'))
const HospitalSignup = lazy(() => import('./components/hospital/account/signup/HospitalSignup'))
const DoctorSignup = lazy(() => import('./components/doctor/account/signup/DoctorSignup'))
const DoctorLogin = lazy(() => import('./components/doctor/account/login/DoctorLogin'))
const HospitalLogin = lazy(() => import('./components/hospital/account/login/HospitalLogin'))
const Layout = lazy(() => import('./components/layout/Layout'))
const PatientLogin = lazy(() => import('./components/patient/accounts/login/PatientLogin'))
const PatientChangePassword = lazy(() => import('./components/patient/accounts/change-password/PatientChangePassword'))
const PatientForgotPassword = lazy(() => import('./components/patient/accounts/forgot-password/PatientForgotPassword'))
const PatientUserForm = lazy(() => import('./components/patient/multistep/PatientUserForm'))
const HospitalUserForm = lazy(() => import('./components/hospital/multistep/HospitalUserForm'))
const DoctorUserForm = lazy(() => import('./components/doctor/multistep/DoctorUserForm'))
const PatientDashboard = lazy(() => import('./components/patient/dashboard/PatientDashboard'))
const HospitalDashboard = lazy(() => import('./components/hospital/dashboard/HospitalDashboard'))
const LandingPage = lazy(() => import('./components/landingPage/LandingPage'))
const ECard = lazy(() => import('./components/patient/ecard/ECard'))
const ImageUpload = lazy(() => import('./components/patient/biometric/image-upload/ImageUpload'))
const DoctorPublicPage = lazy(() => import('./components/doctor/dashboard/DoctorPublicPage'))
const SearchHospital = lazy(() => import('./components/hospital/search/SearchHospital'))
const Notifications = lazy(() => import('./components/notification/Notifications'))
const PrivacyPolicy = lazy(() => import('./components/privacy/privacypolicy/PrivacyPolicy'))
const Help = lazy(() => import('./components/privacy/help/Help'))
const FacialBiometricSearch = lazy(() => import('./components/patient/biometric/search/FacialBiometricSearch'))
const DetailAppointment = lazy(() => import('./components/hospital/dashboard/components/detailAppointment/DetailAppointment'))
const Treatments = lazy(() => import('./components/patient/dashboard/components/treatments/Treatments'))
const SearchDoctors = lazy(() => import('./components/doctor/search/SearchDoctors'))
const HospitalList = lazy(() => import('./components/patient/dashboard/components/hospitallist/HospitalList'))
const ECardForm = lazy(() => import('./components/patient/ecard/form/ECardForm'))
const AppointmentForm = lazy(() => import('./components/hospital/dashboard/components/appointmentform/AppointmentForm'))
const HospitalPublicPage = lazy(() => import('./components/hospital/hospitalpublic/HospitalPublicPage'))
const PatientUpdateForm = lazy(() => import('./components/patient/multistep/UpdateUserData'));
const ReusableECard = lazy(() => import('./components/patient/reusable-ecard/ECard'))

type ReactProps = {
  patient: PatientState
}

type Props = {
  children: React.ReactNode
  path: string
  exact: boolean
  isAuthenticated: boolean

}

const PrivateRoute = (props: Props) => {
  const { isAuthenticated } = props

  return isAuthenticated ? (
    <Route path={props.path} exact={props.exact}>
      {props.children}
    </Route>
  ) : (
    <Redirect to="/login" />
  )
}

const Routes = (prop: ReactProps) => {
  const { patient: { isAuthenticated } } = prop

  return (
    <Router>
      <Suspense fallback={<Spinner />}>
        <Switch>
          <PrivateRoute path="/" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientDashboard />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/e-card" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <ECard />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/change-password" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientChangePassword />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute exact isAuthenticated={isAuthenticated} path="/user-onboarding">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientUserForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/user-onboarding" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientUserForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/edit-profile" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientUpdateForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/hospital-onboarding" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalUserForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/doctor-onboarding" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DoctorUserForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute path="/image-upload" exact isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <ImageUpload />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute exact path="/patient-userform" isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientUserForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute exact path="/detail-appointment" isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DetailAppointment />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <PrivateRoute exact path="/ecardform" isAuthenticated={isAuthenticated}>
            <Layout>
              <Suspense fallback={<Spinner />}>
                <ECardForm />
              </Suspense>
            </Layout>
          </PrivateRoute>
          <Route exact path="/register">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientSignup />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/login">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientLogin />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/forgot-password">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PatientForgotPassword />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/doctor-signup">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DoctorSignup />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/doctor-login">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DoctorLogin />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/hospital-signup">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalSignup />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/hospital-login">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalLogin />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/landing">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <LandingPage />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/hospital-dashboard">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalDashboard />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/facial-biometric-search">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <FacialBiometricSearch />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/doctor-public-page">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DoctorPublicPage />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/search-hospital">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <SearchHospital />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/patient-notification">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <Notifications />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/privacy-policy">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <PrivacyPolicy />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/help">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <Help />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/hospital-list">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalList />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/treatment-list">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <Treatments />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/search-doctor">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <SearchDoctors />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/e-card-search">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <ReusableECard />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/appointmentform">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <AppointmentForm />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/doctor-form">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <DoctorUserForm />
              </Suspense>
            </Layout>
          </Route>
          <Route exact path="/hospital-public">
            <Layout>
              <Suspense fallback={<Spinner />}>
                <HospitalPublicPage />
              </Suspense>
            </Layout>
          </Route>
        </Switch>
        <ToastContainer />
      </Suspense>
    </Router>
  )
}

interface AuthState {
  auth: {
    patientLogin: PatientState
  }
}

const mapStateToProps = (state: AuthState) => ({
  patient: state.auth.patientLogin,
})

export default connect(mapStateToProps)(Routes)
